(() => {
  const existing = window.Area27MobileVideoAutoplay;
  if (existing) {
    existing.refresh();
    return;
  }

  const players = new Set();
  const visiblePlayers = new WeakSet();
  const preparedPlayers = new WeakSet();
  const retryTimers = new WeakMap();

  const getVimeoUrl = (iframe) => {
    const raw = iframe.getAttribute('src');
    if (!raw) return null;

    try {
      const url = new URL(raw, window.location.href);
      if (url.hostname !== 'player.vimeo.com' || !url.pathname.startsWith('/video/')) return null;
      return url;
    } catch (_) {
      return null;
    }
  };

  const isSilentAutoplay = (url) => {
    if (!url || url.searchParams.get('autoplay') !== '1') return false;
    return url.searchParams.get('muted') === '1'
      || url.searchParams.get('mute') === '1'
      || url.searchParams.get('background') === '1';
  };

  const postMethod = (iframe, method, value) => {
    if (!iframe.contentWindow) return;
    const message = value === undefined ? { method } : { method, value };
    iframe.contentWindow.postMessage(JSON.stringify(message), 'https://player.vimeo.com');
  };

  const clearRetries = (iframe) => {
    const timers = retryTimers.get(iframe) || [];
    timers.forEach((timer) => window.clearTimeout(timer));
    retryTimers.delete(iframe);
  };

  const requestPlayback = (iframe) => {
    if (document.hidden || !visiblePlayers.has(iframe)) return;
    clearRetries(iframe);

    const attempt = () => {
      if (document.hidden || !visiblePlayers.has(iframe)) return;
      postMethod(iframe, 'setVolume', 0);
      postMethod(iframe, 'play');
    };

    attempt();
    retryTimers.set(iframe, [400, 1200, 2600].map((delay) => window.setTimeout(attempt, delay)));
  };

  const pausePlayback = (iframe) => {
    clearRetries(iframe);
    postMethod(iframe, 'pause');
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const iframe = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio > 0) {
            visiblePlayers.add(iframe);
            requestPlayback(iframe);
          } else {
            visiblePlayers.delete(iframe);
            pausePlayback(iframe);
          }
        });
      }, { rootMargin: '180px 0px', threshold: 0.01 })
    : null;

  const prepare = (iframe) => {
    if (!(iframe instanceof HTMLIFrameElement) || preparedPlayers.has(iframe)) return;

    const url = getVimeoUrl(iframe);
    if (!isSilentAutoplay(url)) return;

    preparedPlayers.add(iframe);
    players.add(iframe);
    iframe.dataset.a27MobileAutoplay = 'true';

    url.searchParams.set('autoplay', '1');
    url.searchParams.set('muted', '1');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('autopause', '0');
    url.searchParams.delete('mute');

    const normalizedSrc = url.toString();
    if (iframe.src !== normalizedSrc) iframe.src = normalizedSrc;

    const allow = iframe.getAttribute('allow') || '';
    if (!/(^|;)\s*autoplay\s*(;|$)/i.test(allow)) {
      iframe.setAttribute('allow', `autoplay; ${allow}`.replace(/;\s*$/, ''));
    }

    iframe.addEventListener('load', () => requestPlayback(iframe));
    if (observer) observer.observe(iframe);
    else {
      visiblePlayers.add(iframe);
      requestPlayback(iframe);
    }
  };

  const refresh = (root = document) => {
    if (root instanceof HTMLIFrameElement) prepare(root);
    root.querySelectorAll?.('iframe[src*="player.vimeo.com/video/"]').forEach(prepare);
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) refresh(node);
      });
      if (mutation.type === 'attributes') prepare(mutation.target);
    });
  });

  window.Area27MobileVideoAutoplay = { refresh };
  refresh();
  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src']
  });

  document.addEventListener('visibilitychange', () => {
    players.forEach((iframe) => {
      if (document.hidden) pausePlayback(iframe);
      else if (visiblePlayers.has(iframe)) requestPlayback(iframe);
    });
  });
  document.addEventListener('shopify:section:load', () => refresh());
})();
